import { ReactNode } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Button, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

import { LUBAV_PANEL_MIN_WIDTH_PX } from './ResizableDivider';
import { OperatingAsChip } from '../PageShell/OperatingAsChip';

export const HEADER_HEIGHT = 102;

type HeaderProps = Readonly<{
  title: string;
  description?: string;
  stickerUrl: string | null;
  leftComponent?: ReactNode;
  actionButtons?: ReactNode;
  onNewChat?: () => void;
  newChatDisabled?: boolean;
  newChatLabel: string;
  newChatOnLeft?: boolean;
  leftPanelWidth?: number;
  isResizing?: boolean;
  sx?: SxProps<Theme>;
}>;

export const Header = ({
  title,
  description,
  stickerUrl,
  leftComponent,
  actionButtons,
  onNewChat,
  newChatDisabled,
  newChatLabel,
  newChatOnLeft = false,
  leftPanelWidth,
  isResizing = false,
  sx,
}: HeaderProps) => {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const sizeLeftToPanel = newChatOnLeft && leftPanelWidth != null;

  const newChatButton = onNewChat && (
    <Button
      id="lubav-header-new-chat-button"
      hierarchy="Primary"
      size={isMobile ? 'sm' : 'md'}
      startIcon={
        newChatOnLeft ? <AppIcon iconName="Plus" diameter="20px" color="currentColor" /> : undefined
      }
      onClick={onNewChat}
      disabled={newChatDisabled}
    >
      {newChatLabel}
    </Button>
  );

  return (
    <Box sx={[containerSx(sizeLeftToPanel), ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box sx={sizeLeftToPanel ? leftPanelSizedSx(leftPanelWidth, isResizing) : leftContentSx}>
        {stickerUrl && <Box sx={stickerSx} component="img" src={stickerUrl} alt="Sticker" />}
        <Box sx={textContainerSx}>
          <Box sx={titleRowSx}>
            <Typography
              variant={isMobile ? 'text-lg-semibold' : 'display-xs-semibold'}
              sx={ellipsisSx}
            >
              {title}
            </Typography>
            <OperatingAsChip id="page-header-operating-as" />
          </Box>
          {description && (
            <Typography
              variant={isMobile ? 'text-sm-regular' : 'text-md-regular'}
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              sx={ellipsisSx}
            >
              {description}
            </Typography>
          )}
        </Box>
        {newChatOnLeft && newChatButton}
        {leftComponent}
      </Box>
      <Box sx={rightContentSx(sizeLeftToPanel)}>
        {actionButtons && <Box sx={actionButtonsWrapperSx}>{actionButtons}</Box>}
        {!newChatOnLeft && newChatButton}
      </Box>
    </Box>
  );
};

const containerSx =
  (sizeLeftToPanel: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    ...(sizeLeftToPanel
      ? {}
      : {
          paddingLeft: backrs.spacing['spacing-xl'],
          paddingRight: {
            xs: backrs.spacing['spacing-xl'],
            sm: backrs.spacing['spacing-3xl'],
          },
        }),
    paddingY: {
      xs: backrs.spacing['spacing-sm'],
      sm: backrs.spacing['spacing-xl'],
    },
    justifyContent: 'space-between',
    gap: {
      xs: backrs.spacing['spacing-xs'],
      sm: backrs.spacing['spacing-3xl'],
    },
    backgroundColor: {
      xs: backrs.colors['Colors/Base/White'],
      md: 'transparent',
    },
    borderBottom: {
      xs: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
      md: 'none',
    },
  });

const leftContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-3xl'],
  },
  minWidth: 0,
  flex: 1,
});

const leftPanelSizedSx =
  (leftPanelWidth: number, isResizing: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
    width: { xs: 'auto', md: `${leftPanelWidth}%` },
    minWidth: { xs: 0, md: LUBAV_PANEL_MIN_WIDTH_PX },
    flex: { xs: 1, md: 'unset' },
    flexShrink: 0,
    pl: backrs.spacing['spacing-xl'],
    pr: backrs.spacing['spacing-lg'],
    ...(isResizing ? {} : { transition: 'width 0.3s ease-in-out' }),
  });

const rightContentSx =
  (sizeLeftToPanel: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    gap: backrs.spacing['spacing-lg'],
    ...(sizeLeftToPanel && {
      pr: backrs.spacing['spacing-xl'],
    }),
  });

const actionButtonsWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  '& .MuiTabs-root': {
    display: 'flex',
    alignItems: 'center',
  },
});

const stickerSx: SxProps<Theme> = {
  height: {
    xs: '60px',
    sm: '70px',
  },
  width: 'auto',
  maxWidth: 'none',
  flexShrink: 0,
  objectFit: 'contain',
};

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const textContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
  gap: '4px',
};

const ellipsisSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
